/**
 * Smaller components extracted from RuleList for better organization and testability.
 */

import { ClockIcon, MailIcon } from '@heroicons/react/outline';
import { getTagStyles } from '../../lib/colors';
import { DEFAULT_TAG_COLOR, isValidTimeString } from './automationMetadata';
import { ActionDisplayItem } from './RuleList';

/**
 * Props for the ActionBadge component.
 */
interface ActionBadgeProps {
  /** The action display information */
  action: ActionDisplayItem;
  /** Index of this action (for "and" separator) */
  index: number;
}

/**
 * Displays a single action as a badge with icon and optional details.
 *
 * @remarks
 * - Shows template name for email actions
 * - Shows form name and contact type for send_contact_form actions
 * - Shows tag name/color for tag assignment actions
 * - Shows fallback label for other actions
 * - Displays "and" separator between multiple actions
 */
export function ActionBadge({ action, index }: ActionBadgeProps) {
  const ActionIcon = action.icon;
  const tagStyles = action.tagName
    ? getTagStyles(action.tagColor ?? DEFAULT_TAG_COLOR)
    : null;

  return (
    <span className="inline-flex items-center gap-2">
      {index > 0 && (
        <span className="text-neutral-120 text-[11px] font-medium">and</span>
      )}
      <span className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-25 px-2 py-1 text-[11px] text-neutral-150">
        <ActionIcon className="h-3.5 w-3.5 shrink-0 text-green-125" />
        {action.formName != null ? (
          <>
            Send form: <strong>{action.formName}</strong>
            {action.contactTypeLabel ? (
              <>
                {' '}
                to <strong>{action.contactTypeLabel}</strong>
              </>
            ) : null}
          </>
        ) : action.contactTypeLabel != null ? (
          <>
            Send form to <strong>{action.contactTypeLabel}</strong>
          </>
        ) : action.templateName != null ? (
          <>
            Send Mail: <strong>{action.templateName}</strong>
          </>
        ) : action.tagName != null && tagStyles ? (
          <>
            Apply tag{' '}
            <span
              className="inline-flex rounded-full px-1.5 py-0.5 text-[10.5px] font-medium"
              style={tagStyles}
            >
              {action.tagName}
            </span>
          </>
        ) : (
          action.label
        )}
      </span>
    </span>
  );
}

/**
 * Props for the TriggerBadge component.
 */
interface TriggerBadgeProps {
  /** Display label for the trigger */
  label: string;
  /** Icon component for the trigger type */
  icon: React.ComponentType<{ className?: string }>;
  /** Optional detail after the label (e.g. service name), shown bold like Send Mail template names */
  labelDetail?: string;
  /** Optional tag name if trigger is tag-based */
  tagName?: string;
  /** Optional tag color styles */
  tagStyles?: React.CSSProperties;
  /** Optional extra AND tags on time-based triggers */
  andTags?: Array<{ name: string; color?: string }>;
}

/**
 * Displays the trigger badge with icon and optional tag information.
 */
export function TriggerBadge({
  label,
  icon: Icon,
  labelDetail,
  tagName,
  tagStyles,
  andTags,
}: TriggerBadgeProps) {
  const tagFilter =
    andTags && andTags.length > 0 ? (
      <>
        {' '}
        for clients tagged with{' '}
        {andTags.map((tag, index) => (
          <span key={`${tag.name}-${index}`}>
            {index > 0 ? ' and ' : ''}
            <span
              className="inline-flex rounded-full px-1.5 py-0.5 text-[10.5px] font-medium"
              style={getTagStyles(tag.color ?? DEFAULT_TAG_COLOR)}
            >
              {tag.name}
            </span>
          </span>
        ))}
      </>
    ) : null;

  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-1.5 rounded-full border border-primary-100 bg-primary-25 px-2 py-1 text-[11px] font-normal text-neutral-150">
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {tagName != null && tagStyles ? (
        <>
          <span
            className="inline-flex rounded-full px-1.5 py-0.5 text-[10.5px] font-medium"
            style={tagStyles}
          >
            {tagName}
          </span>{' '}
          tag is applied
        </>
      ) : labelDetail != null ? (
        <>
          {label}: <strong>{labelDetail}</strong>
          {tagFilter}
        </>
      ) : (
        <>
          {label}
          {tagFilter}
        </>
      )}
    </span>
  );
}

interface TimeBadgeProps {
  time: string;
}

export function TimeBadge({ time }: TimeBadgeProps) {
  if (!isValidTimeString(time)) {
    return (
      <span className="border-red-200 text-red-600 inline-flex shrink-0 items-center gap-1.5 rounded-full border bg-red-50 px-2 py-1 text-[11px]">
        <ClockIcon className="h-3.5 w-3.5 shrink-0" />
        Invalid time
      </span>
    );
  }

  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const display = `${displayHours}:${minutes
    .toString()
    .padStart(2, '0')} ${period}`;

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-primary-100 bg-primary-25 px-2 py-1 text-[11px] text-neutral-150">
      <ClockIcon className="h-3.5 w-3.5 shrink-0" />
      {display}
    </span>
  );
}

/**
 * Props for the WorkflowSummary component.
 */
interface WorkflowSummaryProps {
  timePill?: React.ReactElement;
  triggerBadge: React.ReactElement;
  actionBadges: React.ReactElement[];
}

/**
 * Displays the workflow summary with trigger and actions.
 */
export function WorkflowSummary({
  timePill,
  triggerBadge,
  actionBadges,
}: WorkflowSummaryProps) {
  return (
    <div className="mt-2 rounded-md bg-neutral-50 p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        {triggerBadge}
        {timePill}
        <span className="text-neutral-120 shrink-0 text-[11px]">→</span>
        {actionBadges.length === 0 ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-25 px-2 py-1 text-[11px] text-neutral-150">
            <MailIcon className="h-3.5 w-3.5 shrink-0 text-green-125" />
            No action
          </span>
        ) : (
          actionBadges
        )}
      </div>
    </div>
  );
}
